import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getMotions } from '../services/viewingMotionsServices'
import { FIVE_MINUTES_IN_MS } from '../configuration/globalConstants'
import { useSessionStore } from '../store/sessionStore'
import useCalculateStatistics from './useCalculateStatistics'

export function useMotions() {
  const [page, setPage] = useState(0)
  const { user, myStore } = useSessionStore()
  const { formatTime } = useCalculateStatistics()

  const query = useQuery({
    queryKey: ['motions', myStore.id, user, page ],
    queryFn: () => getMotions({ storeId: myStore.id, userId: user, page }),
    placeholderData: keepPreviousData,
    staleTime: FIVE_MINUTES_IN_MS,
    enabled: !!myStore && !!user,
    select: (response) =>
      ({...response,
        content:response.content.map((mot) => {
        const motion = {
          id: mot.id,
          creationDate: mot.creationDate.slice(0, 16).replace('T', ' / '),
          name: mot.map !== null ? mot.map.name : mot.section.name,
          level: mot.map !== null ? 0 : mot.section.level,
          map: mot.map !== null ? mot.map.id : mot.section.map_id,
          ...(mot.section !== null && mot.section.level === 1
            ? { section: mot.section.id }
            : {}),
          ...(mot.section !== null && mot.section.level === 2
            ? {
                section: mot.section.parentSection_id,
                subsection: mot.section.id,
              }
            : {}),
          trackingTime: formatTime(mot.trackingTime),
          state: mot.state,
        }
        return motion
      })}),
  })

  // Prefetch the next page for smooth navigation
  const qc = useQueryClient()
  useEffect(() => {
    const data = query.data
    if (!data || data.last) return

    qc.prefetchQuery({
      queryKey: [
        'motions',
        { storeId: myStore.id, userId: user, page: page + 1 },
      ],
      queryFn: () => getMotions({ storeId: myStore.id, userId: user, page }),
      staleTime: FIVE_MINUTES_IN_MS,
    })
  }, [query?.data, page, myStore.id, user, qc])

  const data = query.data
  const motions = data?.content ?? []
  const totalPages = data?.totalPages ?? 0
  const totalElements = data?.totalElements ?? 0

  const goto = (p) => {
    setPage(Math.max(0, Math.min(p, Math.max(0, totalPages - 1))))
  }

  return {
    motions,
    page,
    totalPages,
    totalElements,
    goto,
    ...query,
  }
}
